<template>
  <v-container class="my-5">
    <!-- Header -->
    <v-card-title class="px-0 mb-2">
      <v-icon large color="primary" class="mr-2">mdi-domain</v-icon>
      <span class="headline font-weight-bold">{{ $t('Areas') }}</span>
      <v-spacer></v-spacer>
      <v-btn
        v-if="(getProfile.role === 1) && (getProfile.department_manager === true || getProfile.staff)"
        color="primary"
        depressed
        @click="openCreateDialog"
      >
        <v-icon left>mdi-plus</v-icon>
        {{ $t('add_area') }}
      </v-btn>
    </v-card-title>

    <!-- Loading -->
    <v-row v-if="loadingAreas" justify="center" class="py-10">
      <v-progress-circular indeterminate color="primary" size="52"></v-progress-circular>
    </v-row>

    <!-- Cards grid -->
    <v-row v-else-if="areas.length > 0" class="mt-1">
      <v-col
        v-for="area in areas"
        :key="area.id"
        cols="12" sm="6" md="4" lg="3"
      >
        <v-card
          class="area-card"
          style="border-radius: 16px; overflow: hidden; transition: box-shadow 0.25s ease, transform 0.25s ease; cursor: pointer;"
          elevation="2"
          @click="getProfile.role === 1 ? openEditDialog(area) : null"
        >
          <!-- Color stripe top -->
          <div :style="`height: 8px; background: ${area.color || '#BDBDBD'};`"></div>

          <v-card-text class="pt-4 pb-2">
            <div class="d-flex align-center mb-3">
              <!-- Color avatar -->
              <v-avatar :color="area.color || '#BDBDBD'" size="48" class="mr-3 elevation-1">
                <v-icon dark size="24">mdi-map-marker-radius</v-icon>
              </v-avatar>
              <div class="flex-grow-1">
                <div class="subtitle-1 font-weight-bold grey--text text--darken-3 mb-0" style="line-height: 1.2;">
                  {{ area.name }}
                </div>
                <div v-if="area.color" class="caption grey--text">{{ area.color }}</div>
              </div>
            </div>

            <!-- Occupancy indicator -->
            <div v-if="occupancyMap[area.id]">
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="caption grey--text">{{ $t('occupancy') || 'Ocupación' }}</span>
                <v-chip
                  :color="getStatusColor(occupancyMap[area.id].status)"
                  x-small dark
                >
                  {{ getStatusLabel(occupancyMap[area.id].status) }}
                </v-chip>
              </div>
              <v-progress-linear
                :value="getOccupancyPercent(occupancyMap[area.id])"
                :color="getStatusColor(occupancyMap[area.id].status)"
                height="6"
                rounded
                class="mb-1"
              ></v-progress-linear>
              <div class="caption grey--text d-flex justify-space-between">
                <span>{{ occupancyMap[area.id].current_count }} presentes</span>
                <span>Cap. {{ occupancyMap[area.id].max_capacity }}</span>
              </div>
            </div>
            <div v-else-if="loadingOccupancy[area.id]" class="text-center py-1">
              <v-progress-circular indeterminate color="grey" size="16" width="2"></v-progress-circular>
            </div>
            <div v-else class="caption grey--text text--lighten-1">
              {{ $t('no_occupancy_data') || 'Sin datos de ocupación' }}
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <!-- Actions -->
          <v-card-actions class="px-3 py-1" @click.stop>
            <v-btn
              v-if="getProfile.role === 1 && (getProfile.permissions && getProfile.permissions.includes('sports'))"
              icon
              x-small
              :to="`/sports-area/${area.id}/checkin`"
              color="primary"
              title="Ver ocupación"
              @click.stop
            >
              <v-icon x-small>mdi-chart-bar</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <template v-if="getProfile.role === 1">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon x-small class="mr-1" v-bind="attrs" v-on="on" @click.stop="openEditDialog(area)">
                    <v-icon x-small color="blue-grey darken-1">mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('modificar') }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop="confirmDelete(area)">
                    <v-icon x-small color="red lighten-2">mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('delete') || 'Eliminar' }}</span>
              </v-tooltip>
            </template>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Empty state -->
    <v-row v-else justify="center" class="py-10">
      <v-flex class="text-center grey--text">
        <v-icon large color="grey lighten-2">mdi-domain</v-icon>
        <h3 class="mt-2">{{ $t('no_areas_registered') || 'No hay áreas registradas aún.' }}</h3>
      </v-flex>
    </v-row>

    <!-- ===== Dialog Create / Edit ===== -->
    <v-dialog v-model="dialog" max-width="700px" persistent>
      <v-form ref="form" v-model="valid" @submit.prevent="save">
        <v-card style="border-radius: 16px; overflow: hidden;">
          <!-- Header -->
          <div class="d-flex align-center px-5 pt-5 pb-4" style="gap: 14px;">
            <v-avatar :color="editedItem.color || '#BDBDBD'" size="44" class="elevation-1">
              <v-icon dark>mdi-map-marker-radius</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
              <div class="overline grey--text mb-0">{{ isUpdate ? ($t('modificar') || 'Editar') : ($t('add_area') || 'Agregar') }}</div>
              <div class="title font-weight-bold grey--text text--darken-3">{{ editedItem.name || ($t('add_area') || 'Nueva Área') }}</div>
            </div>
            <v-btn icon small @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
          </div>

          <v-divider></v-divider>

          <v-card-text class="pt-5 pb-3">
            <v-row>
              <!-- Campos -->
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="editedItem.name"
                  :label="'*' + ($t('nombre') || 'Nombre')"
                  prepend-icon="mdi-format-title"
                  outlined
                  :rules="[rules.required]"
                  :error-messages="errores.name"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model.number="editedItem.max_capacity"
                  :label="$t('max_capacity') || 'Capacidad Máxima'"
                  prepend-icon="mdi-account-group"
                  outlined
                  type="number"
                  min="1"
                  :hint="$t('max_capacity_hint') || 'Máximo de personas simultáneas'"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <!-- Color Picker -->
              <v-col cols="12" md="7" class="d-flex flex-column">
                <div class="caption grey--text mb-2 ml-2">{{ $t('select_color') || 'Color del Área' }}</div>
                <div class="color-picker-wrapper">
                  <v-color-picker
                    v-model="editedItem.color"
                    hide-mode-switch
                    dot-size="28"
                    canvas-height="200"
                    class="elevation-0 full-width-color-picker"
                  ></v-color-picker>
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions class="px-5 py-3">
            <v-btn text small color="grey darken-1" @click="closeDialog">{{ $t('cerrar') }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" depressed small :loading="loading" type="submit" :disabled="!valid">
              {{ isUpdate ? ($t('modificar') || 'Guardar') : ($t('agregar') || 'Agregar') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- ===== Dialog Delete ===== -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">{{ $t('msg_delete_notice') || 'Eliminar Área' }}</v-card-title>
        <v-card-text>{{ $t('msg_accion_deshacer') || '¿Estás seguro? Esta acción no se puede deshacer.' }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="deleteDialog = false">{{ $t('cancelar') || 'No' }}</v-btn>
          <v-btn color="red darken-1" :loading="loading" text @click="deleteAreaConfirm">{{ $t('Si') || 'Sí' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import { mapGetters, mapState } from 'vuex'

const API_BASE_URL = 'https://clubtest.intrare.app'

export default {
  name: 'areas',
  computed: {
    ...mapGetters(['getProfile']),
    ...mapState(['language']),
    rules () {
      return {
        required: v => !!v || (this.$t('required_field') || 'Campo requerido')
      }
    }
  },
  data: () => ({
    areas: [],
    occupancyMap: {},
    loadingOccupancy: {},
    loadingAreas: false,
    loading: false,
    dialog: false,
    deleteDialog: false,
    valid: true,
    isUpdate: false,
    editedItem: {
      id: null,
      name: '',
      color: '#4CAF50',
      max_capacity: null
    },
    defaultItem: {
      id: null,
      name: '',
      color: '#4CAF50',
      max_capacity: null
    },
    areaToDelete: null,
    errores: []
  }),
  created () {
    this.loadAreas()
  },
  methods: {
    apiCall (method, endpoint, data = null) {
      const config = {
        method,
        url: `${API_BASE_URL}${endpoint}`,
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      if (data) config.data = data
      return axios(config)
    },
    async loadAreas () {
      this.loadingAreas = true
      try {
        const res = await this.apiCall('get', '/area/')
        this.areas = res.data
        // Load occupancy for each area
        this.areas.forEach(area => this.loadAreaOccupancy(area.id))
      } catch (e) {
        console.error('Error loading areas:', e)
      } finally {
        this.loadingAreas = false
      }
    },
    async loadAreaOccupancy (areaId) {
      this.$set(this.loadingOccupancy, areaId, true)
      try {
        const res = await this.apiCall('get', `/area/${areaId}/occupancy/`)
        this.$set(this.occupancyMap, areaId, res.data)
      } catch (e) {
        // Occupancy might not exist yet — silently ignore
      } finally {
        this.$set(this.loadingOccupancy, areaId, false)
      }
    },
    getOccupancyPercent (occ) {
      if (!occ || !occ.max_capacity) return 0
      return Math.round(occ.occupancy_level * 100)
    },
    getStatusColor (status) {
      switch (status) {
        case 'low': return 'green'
        case 'medium': return 'orange'
        case 'high': return 'red'
        default: return 'grey'
      }
    },
    getStatusLabel (status) {
      switch (status) {
        case 'low': return this.$t('occupancy_low') || 'Baja'
        case 'medium': return this.$t('occupancy_medium') || 'Media'
        case 'high': return this.$t('occupancy_high') || 'Alta'
        default: return status || '—'
      }
    },
    openCreateDialog () {
      this.isUpdate = false
      this.errores = []
      this.editedItem = Object.assign({}, this.defaultItem)
      if (this.$refs.form) this.$refs.form.resetValidation()
      this.dialog = true
    },
    openEditDialog (area) {
      this.isUpdate = true
      this.errores = []
      this.editedItem = {
        id: area.id,
        name: area.name,
        color: area.color || '#4CAF50',
        max_capacity: this.occupancyMap[area.id] ? this.occupancyMap[area.id].max_capacity : null
      }
      if (this.$refs.form) this.$refs.form.resetValidation()
      this.dialog = true
    },
    closeDialog () {
      this.dialog = false
    },
    async save () {
      if (!this.$refs.form.validate()) return
      this.loading = true
      this.errores = []
      try {
        if (this.isUpdate) {
          // Update name and color
          await this.apiCall('put', `/area/${this.editedItem.id}`, {
            idHousingUnit: this.getProfile.idHousingUnit,
            name: this.editedItem.name,
            color: this.editedItem.color
          })
          // Update capacity via config endpoint if provided
          if (this.editedItem.max_capacity !== null && this.editedItem.max_capacity !== '') {
            await this.apiCall('put', `/area/${this.editedItem.id}/config/`, {
              max_capacity: Number(this.editedItem.max_capacity),
              name: this.editedItem.name,
              color: this.editedItem.color
            })
          }
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('msg_area_updated') || 'Área actualizada')
        } else {
          const res = await this.apiCall('post', '/area/', {
            idHousingUnit: this.getProfile.idHousingUnit,
            name: this.editedItem.name,
            color: this.editedItem.color
          })
          // Configure capacity on newly created area if provided
          if (this.editedItem.max_capacity !== null && this.editedItem.max_capacity !== '' && res.data && res.data.id) {
            await this.apiCall('put', `/area/${res.data.id}/config/`, {
              max_capacity: Number(this.editedItem.max_capacity),
              name: this.editedItem.name,
              color: this.editedItem.color
            })
          }
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('msg_area_added') || 'Área agregada')
        }
        this.closeDialog()
        await this.loadAreas()
      } catch (error) {
        if (error.response && error.response.status === 400 && error.response.data) {
          this.errores = error.response.data
        } else {
          console.error('Error saving area:', error)
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_change_message', 'Ocurrió un error al guardar el área')
        }
      } finally {
        this.loading = false
      }
    },
    confirmDelete (area) {
      this.areaToDelete = area
      this.deleteDialog = true
    },
    async deleteAreaConfirm () {
      if (!this.areaToDelete) return
      this.loading = true
      try {
        await this.apiCall('delete', `/area/${this.areaToDelete.id}/`)
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_color', 'warning')
        this.$store.commit('snackbar_change_message', this.$t('msg_area_deleted') || 'Área eliminada')
        await this.loadAreas()
      } catch (e) {
        console.error('Error deleting area:', e)
      } finally {
        this.loading = false
        this.deleteDialog = false
        this.areaToDelete = null
      }
    }
  }
}
</script>

<style scoped>
.area-card:hover {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12) !important;
  transform: translateY(-3px);
}

/* Color picker full-width */
.color-picker-wrapper {
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

::v-deep .full-width-color-picker {
  width: 100% !important;
  max-width: 100% !important;
}

::v-deep .full-width-color-picker .v-color-picker__canvas,
::v-deep .full-width-color-picker .v-color-picker__controls,
::v-deep .full-width-color-picker .v-color-picker__edit {
  width: 100% !important;
  max-width: 100% !important;
}

.color-picker-wrapper ::v-deep .v-color-picker {
  width: 100% !important;
  max-width: 100% !important;
  box-shadow: none !important;
}

.color-picker-wrapper ::v-deep .v-picker__body {
  width: 100% !important;
  max-width: 100% !important;
  padding: 0 !important;
}

.color-picker-wrapper ::v-deep .v-color-picker__canvas {
  width: 100% !important;
  max-width: 100% !important;
}

.color-picker-wrapper ::v-deep .v-color-picker__canvas canvas {
  width: 100% !important;
  height: 200px !important;
}

.color-picker-wrapper ::v-deep .v-color-picker__controls {
  width: 100% !important;
  max-width: 100% !important;
  padding: 8px 16px !important;
}

.color-picker-wrapper ::v-deep .v-color-picker__edit {
  width: 100% !important;
  max-width: 100% !important;
}
</style>
